/**
 * How a meal plan is laid out under each meal: a list of alternatives, or a
 * list of days.
 *
 * Option-based is what the builder has always been — Breakfast holds Option 1,
 * Option 2, Option 3, and the client eats whichever they like. Day-based is
 * the same meal holding Day 1 to Day 7, one set of dishes each, so a week is
 * written out rather than a day with choices in it.
 *
 * The backend owns the choice: it is sent with the create and comes back on
 * every plan, and it decides the shape a plan reads back in — a day-based
 * plan's meals carry `days` where an option-based plan's carry `options`. The
 * app reads both into the one list a `MealSlot` holds, so the two are the same
 * data on screen and what differs is what the list is called, how many of them
 * there may be, and whether the entries can be written yet. See
 * `toPlanStructure` below for the one place the two vocabularies meet.
 */

/** Which of the two layouts a plan is written in. */
export type PlanStructure = "option" | "day";

/**
 * How the backend names the same two. `OPTION_BASED` is a meal holding
 * alternatives; `DAY_BASED` is a meal holding the days of a week.
 *
 * Anything read off the wire is read loosely rather than as this union: it is
 * the backend's vocabulary, a third word in it should not be a type error in a
 * plan list, and the detail endpoint has already been seen sending the shorter
 * `OPTION` for what the list calls `OPTION_BASED`. `toPlanStructure` below is
 * the only place the two vocabularies meet.
 */
export type MealPlanStructureParam = "OPTION_BASED" | "DAY_BASED";

/**
 * The backend's word for how a plan is laid out, in the app's own vocabulary.
 *
 * Read by what it starts with rather than matched against the two spellings,
 * for the reason above: a plan is not worth failing to draw over a word the
 * backend shortened. Anything that is not a day is an option, which is what a
 * plan is when nobody said.
 */
export const toPlanStructure = (
  structure: string | null | undefined
): PlanStructure =>
  typeof structure === "string" && structure.toUpperCase().startsWith("DAY")
    ? "day"
    : "option";

/** And back again, for the create. */
export const toStructureParam = (
  structure: PlanStructure
): MealPlanStructureParam =>
  structure === "day" ? "DAY_BASED" : "OPTION_BASED";

/**
 * The most days a plan can hold. A week, because that is what a day-based plan
 * is for — a rotation the client repeats — and an eighth day would be the
 * second week of a plan nobody asked for.
 *
 * Option-based has no such ceiling: alternatives for one meal are however many
 * the dietitian wants to offer.
 */
export const MAX_PLAN_DAYS = 7;

/** What either structure is called on screen, and what its list is called. */
interface PlanStructureCopy {
  /** The card's heading — "Option-based". */
  title: string;
  /** The line under it, as the Figma has it. */
  description: string;
  /**
   * What one entry in a meal's list is called, capitalised for a heading:
   * "Option" or "Day". Everything the builder writes about the list is built
   * from this — the heading over each card, the button that adds another.
   */
  unit: string;
  /** The pill beside the plan's name in the builder. */
  badge: string;
}

export const PLAN_STRUCTURE_COPY: Record<PlanStructure, PlanStructureCopy> = {
  option: {
    title: "Option-wise",
    description: "Meal slots with multiple options. No day layout.",
    unit: "Option",
    badge: "Option-wise plan",
  },
  day: {
    title: "Day-wise",
    description:
      "Plan your meals by day, with one set of dishes per slot. Add up to 7 days.",
    unit: "Day",
    badge: "Day-wise plan",
  },
};

/**
 * The two, in the order they are offered. `option` first: it is what every
 * plan written so far is, and the one the dialog opens on.
 */
export const PLAN_STRUCTURES: PlanStructure[] = ["option", "day"];

/** The structure a workspace that has never chosen one writes plans in. */
export const DEFAULT_PLAN_STRUCTURE: PlanStructure = "option";

/** Whether a stored or fetched string is one of the two. */
export const isPlanStructure = (value: unknown): value is PlanStructure =>
  value === "option" || value === "day";

/**
 * What the list under a meal is called, at a given length.
 *
 * "Option 3", "Day 3" — the heading over each card and the label on the button
 * that adds the next one both come from here, so the two can never drift into
 * calling the same thing by different names.
 */
export const planUnitLabel = (
  structure: PlanStructure,
  index: number
): string => `${PLAN_STRUCTURE_COPY[structure].unit} ${index}`;

/**
 * Whether another entry may be added to a meal holding `count` of them.
 *
 * Only day-based has an answer other than yes — see `MAX_PLAN_DAYS`.
 */
export const canAddPlanUnit = (
  structure: PlanStructure,
  count: number
): boolean => structure === "option" || count < MAX_PLAN_DAYS;
